declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export interface VoiceResult {
  transcript: string;
  confidence: number;
}

export interface VoiceCommand {
  action: 'navigate' | 'nearest' | 'search' | 'facilityType' | 'call' | 'details' | 'cancel' | 'back';
  params: string[];
  confidence: number;
  // Additional shape used by tests and UI helpers
  type?: string;
  facilityType?: string;
  needsLocation?: boolean;
}

export class VoiceService {
  private recognition: any | null = null;
  private isListening = false;
  private silenceTimer: number | null = null;
  private lastTranscript = '';
  private readonly SILENCE_DELAY = 1500; // 1.5 seconds of silence
  public onResult: ((result: VoiceResult) => void) | null = null;
  public onInterimResult: ((transcript: string) => void) | null = null;
  public onError: ((error: string) => void) | null = null;
  public onStart: (() => void) | null = null;
  public onEnd: (() => void) | null = null;
  public onSilenceDetected: (() => void) | null = null;

  constructor() {
    this.initializeRecognition();
  }

  private initializeRecognition() {
    // Check for browser support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech Recognition not supported in this browser');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.setupRecognition();
  }

  private setupRecognition() {
    if (!this.recognition) return;

    // Configuration for continuous recognition with auto-stop
    this.recognition.continuous = true; // Keep listening for interim results
    this.recognition.interimResults = true; // Get interim results for silence detection
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 3;

    // Handle interim and final results
    this.recognition.onresult = (event: any) => {
      const results = event.results;
      const lastResult = results[results.length - 1];
      const transcript = lastResult[0].transcript;
      
      // Update transcript for both interim and final results
      this.lastTranscript = transcript;
      
      console.log(`🎤 ${lastResult.isFinal ? 'Final' : 'Interim'} result:`, transcript);
      
      // Send interim results to UI for live display
      if (!lastResult.isFinal && this.onInterimResult) {
        this.onInterimResult(transcript);
      }
      
      if (lastResult.isFinal) {
        // Final result - reset silence timer
        this.clearSilenceTimer();
        
        // Start silence detection timer
        this.startSilenceTimer();
      } else {
        // Interim result - user is still speaking, reset timer
        this.clearSilenceTimer();
        this.startSilenceTimer();
      }
    };

    // Handle errors
    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.clearSilenceTimer();
      
      if (this.onError) {
        const errorMessages: Record<string, string> = {
          'no-speech': 'No speech detected. Try again.',
          'audio-capture': 'Microphone not available.',
          'not-allowed': 'Microphone permission denied.',
          'network': 'Network error. Check connection.',
          'aborted': 'Speech recognition aborted.',
        };
        
        this.onError(errorMessages[event.error] || 'Voice recognition error');
      }
      
      this.isListening = false;
      if (this.onEnd) this.onEnd();
    };

    // Handle start
    this.recognition.onstart = () => {
      this.isListening = true;
      this.lastTranscript = '';
      if (this.onStart) this.onStart();
    };

    // Handle end
    this.recognition.onend = () => {
      this.clearSilenceTimer();
      this.isListening = false;
      if (this.onEnd) this.onEnd();
    };
  }

  private startSilenceTimer() {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      if (this.isListening && this.lastTranscript) {
        console.log('🔇 Silence detected, sending transcript:', this.lastTranscript);
        
        // Silence detected - call callback and send final result
        if (this.onSilenceDetected) this.onSilenceDetected();
        
        if (this.onResult) {
          this.onResult({
            transcript: this.lastTranscript,
            confidence: 0.9
          });
        }
        
        // Stop recognition
        this.stop();
      }
    }, this.SILENCE_DELAY);
  }

  private clearSilenceTimer() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  public start(): boolean {
    if (!this.recognition) {
      if (this.onError) {
        this.onError('Voice recognition not supported');
      }
      return false;
    }

    if (this.isListening) {
      return false;
    }

    try {
      this.recognition.start();
      return true;
    } catch (error) {
      console.error('Failed to start recognition:', error);
      if (this.onError) {
        this.onError('Failed to start voice recognition');
      }
      return false;
    }
  }

  public stop() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  public abort() {
    if (this.recognition) {
      this.recognition.abort();
      this.isListening = false;
    }
  }

  public getIsListening(): boolean {
    return this.isListening;
  }

  public isSupported(): boolean {
    return this.recognition !== null;
  }

  public cleanup() {
    this.abort();
    this.clearSilenceTimer();
    this.onResult = null;
    this.onInterimResult = null;
    this.onError = null;
    this.onStart = null;
    this.onEnd = null;
    this.onSilenceDetected = null;
  }
}

// Voice command patterns
const VOICE_PATTERNS = {
  navigate: /(?:navigate to|drive to|go to|take me to|directions to)\s+(?:well\s+)?([a-z0-9\s-]+)/i,
  nearest: /(?:nearest|closest|find nearest)\s+(well|tank battery|battery|compressor|pipeline|facility)/i,
  search: /(?:search for|find|show me|search)\s+(.+)/i,
  facilityType: /(?:show|find|search)\s+(?:all\s+)?(tank batteries|batteries|compressors|pipelines|facilities)/i,
  operator: /(?:wells by|operator|show)\s+(.+)/i,
  call: /call\s+(?:the\s+)?operator/i,
  details: /(?:show|tell me|what's the)\s+details/i,
  cancel: /(?:cancel|stop|never mind|go back)/i,
  back: /(?:go back|return|previous)/i,
};

export function parseVoiceCommand(transcript: string): VoiceCommand | null {
  const normalized = transcript.toLowerCase().trim();
  const hasWellKeyword = /\bwell(s)?\b/.test(normalized);

  // Check each pattern
  for (const [action, pattern] of Object.entries(VOICE_PATTERNS)) {
    const match = normalized.match(pattern);
    if (match) {
      const facilityType = action === 'facilityType'
        ? (match[1] || '').toLowerCase()
        : hasWellKeyword ? 'well' : undefined;
      const needsLocation = /near|closest|nearest/.test(normalized);

      return {
        action: action as VoiceCommand['action'],
        params: match.slice(1),
        confidence: 1.0,
        type: 'search',
        facilityType,
        needsLocation,
      };
    }
  }

  // If no pattern matches but we have text that looks like a well name, treat as search
  // Well names are typically like "Blackjack", "Apache 42", "State 1", etc.
  if (normalized.length > 0 && !/^(cancel|stop|back|help)$/i.test(normalized)) {
    return {
      action: 'search',
      params: [normalized],
      confidence: 0.8,
      type: 'search',
      facilityType: hasWellKeyword ? 'well' : undefined,
      needsLocation: /near|closest|nearest/.test(normalized),
    };
  }

  return null;
}
